package top.wcpe.allincore.dsl.compiler.codegen

import top.wcpe.allincore.dsl.compiler.ast.ClassDecl
import top.wcpe.allincore.dsl.compiler.ast.ClassKind
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.MenuContentAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.TooltipContentAst
import top.wcpe.allincore.dsl.compiler.ast.TooltipDataBinding
import top.wcpe.allincore.dsl.compiler.codegen.internal.ExprSerializer
import top.wcpe.allincore.dsl.compiler.pipeline.ThemeRegistry
import top.wcpe.allincore.dsl.ir.model.ThemeDescriptor
import top.wcpe.allincore.dsl.ir.model.UiBinding
import top.wcpe.allincore.dsl.ir.model.UiIr
import top.wcpe.allincore.dsl.ir.model.UiInterceptRule
import top.wcpe.allincore.dsl.ir.model.UiNode
import top.wcpe.allincore.dsl.ir.model.UiScreenFont
import top.wcpe.allincore.dsl.ir.model.action.IrFun
import top.wcpe.allincore.dsl.ir.model.action.ReservedActions
import top.wcpe.allincore.dsl.ir.model.action.UiAction
import top.wcpe.allincore.dsl.ir.model.animation.UiAnimation
import top.wcpe.allincore.dsl.ir.model.hud.HudLayoutPolicy
import top.wcpe.allincore.dsl.ir.model.hud.HudStackPolicy
import top.wcpe.allincore.dsl.ir.model.hud.HudVisibilityPolicy
import top.wcpe.allincore.dsl.ir.model.hud.UiHudMeta
import top.wcpe.allincore.dsl.ir.model.overlay.ContentRef
import top.wcpe.allincore.dsl.ir.model.overlay.OverlayDescriptor

/**
 * 把整个 [ScreenAst] 转换为 [UiIr]。
 *
 * 组合三个子 emitter（state / fun / element），保持声明顺序。
 */
class ScreenEmitter(
    private val states: StateEmitter = StateEmitter(),
    private val animations: AnimationEmitter = AnimationEmitter(),
    private val funs: FunEmitter = FunEmitter(),
    private val bindings: BindingEmitter = BindingEmitter(),
    private val elements: ElementEmitter = ElementEmitter(bindings),
    private val papi: PapiEmitter = PapiEmitter(),
    private val subscribe: SubscribeEmitter = SubscribeEmitter(),
) {

    companion object {
        /**
         * screen 级 onKey lambda 在 actionMap 中的保留键名（编译期 + 运行期共享）。
         *
         * @see ReservedActions.SCREEN_ON_KEY
         */
        const val SCREEN_ON_KEY_ACTION_NAME: String = ReservedActions.SCREEN_ON_KEY

        /** onKey lambda 强制形参名（编译期 + 运行期共享）。 */
        const val LAMBDA_PARAM_NAME: String = ReservedActions.SCREEN_ON_KEY_PARAM

        /**
         * screen 级 onKeyRelease lambda 在 actionMap 中的保留键名（编译期 + 运行期共享）。
         *
         * @see ReservedActions.SCREEN_ON_KEY_RELEASE
         */
        const val SCREEN_ON_KEY_RELEASE_ACTION_NAME: String = ReservedActions.SCREEN_ON_KEY_RELEASE

        /** onKeyRelease lambda 强制形参名（与 onKey 共用 "event"，保持 DSL 写法对称）。 */
        const val ON_KEY_RELEASE_LAMBDA_PARAM_NAME: String = ReservedActions.SCREEN_ON_KEY_RELEASE_PARAM

        /**
         * RFC-INPUT-EVENT-UNIFICATION Stage 4：
         * screen 级 onMouseDown lambda 在 actionMap 中的保留键名（编译期 + 运行期共享）。
         *
         * @see ReservedActions.SCREEN_ON_MOUSE_DOWN
         */
        const val SCREEN_ON_MOUSE_DOWN_ACTION_NAME: String = ReservedActions.SCREEN_ON_MOUSE_DOWN

        /**
         * onMouseDown lambda 强制形参名（与 onKey 共用 "event"，保持 DSL 写法对称）。
         */
        const val ON_MOUSE_DOWN_LAMBDA_PARAM_NAME: String = ReservedActions.SCREEN_ON_MOUSE_DOWN_PARAM

        /**
         * RFC-MOUSE-EXTENDED-EVENTS Stage A：
         * screen 级 onMouseUp lambda 在 actionMap 中的保留键名（编译期 + 运行期共享）。
         *
         * @see ReservedActions.SCREEN_ON_MOUSE_UP
         */
        const val SCREEN_ON_MOUSE_UP_ACTION_NAME: String = ReservedActions.SCREEN_ON_MOUSE_UP

        /** onMouseUp lambda 强制形参名（与 onMouseDown 共用 "event"，保持 DSL 写法对称）。 */
        const val ON_MOUSE_UP_LAMBDA_PARAM_NAME: String = ReservedActions.SCREEN_ON_MOUSE_UP_PARAM

        /**
         * RFC-MOUSE-EXTENDED-EVENTS Stage B：
         * screen 级 onMouseMove lambda 在 actionMap 中的保留键名（编译期 + 运行期共享）。
         *
         * @see ReservedActions.SCREEN_ON_MOUSE_MOVE
         */
        const val SCREEN_ON_MOUSE_MOVE_ACTION_NAME: String = ReservedActions.SCREEN_ON_MOUSE_MOVE

        /** onMouseMove lambda 强制形参名（与 onMouseUp 共用 "event"，保持 DSL 写法对称）。 */
        const val ON_MOUSE_MOVE_LAMBDA_PARAM_NAME: String = ReservedActions.SCREEN_ON_MOUSE_MOVE_PARAM

        /**
         * Stage 1：screen 级 open 块在 actionMap 中的保留键名。
         *
         * @see ReservedActions.SCREEN_OPEN
         */
        const val SCREEN_OPEN_ACTION_NAME: String = ReservedActions.SCREEN_OPEN

        /**
         * Stage 1：screen 级 close 块在 actionMap 中的保留键名。
         *
         * @see ReservedActions.SCREEN_CLOSE
         */
        const val SCREEN_CLOSE_ACTION_NAME: String = ReservedActions.SCREEN_CLOSE

        /** show 块保留键名（每次显示触发）。 @see ReservedActions.SCREEN_SHOW */
        const val SCREEN_SHOW_ACTION_NAME: String = ReservedActions.SCREEN_SHOW

        /** hide 块保留键名（每次隐藏触发）。 @see ReservedActions.SCREEN_HIDE */
        const val SCREEN_HIDE_ACTION_NAME: String = ReservedActions.SCREEN_HIDE

        private val COLOR_LITERAL = Regex("^#(?:[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$")
    }

    /** 兼容旧入口：不传 themeRegistry 时 availableThemes 为空。 */
    fun emit(screen: ScreenAst): UiIr = emit(screen, null)

    fun emit(screen: ScreenAst, themeRegistry: ThemeRegistry?): UiIr {
        val stateIr = states.emit(screen.state)
        val actionMap = LinkedHashMap<String, UiAction>()
        val animationMap = LinkedHashMap<String, UiAnimation>()
        for (a in screen.animations) {
            animationMap[a.id] = animations.emit(a)
        }
        val funMap = LinkedHashMap<String, IrFun>()
        for (f in screen.funs) {
            val irFun = funs.emit(f)
            funMap[f.name] = irFun
            if (f.params.isEmpty() && f.name !in actionMap) {
                actionMap[f.name] = UiAction(f.name, irFun.bodyBytes)
            }
        }
        // Stage 7b / Stage 8：把 user-defined object fun 写入 functions map，
        // 用 UserObjectMethodDescriptor.funRefKey 生成 key（"Outer#Inner#methodName"），
        // 与 UserObjectMethodDescriptor.resolve / NativeClassResolverPass 产生的
        // FunRefExpr.name 一致；嵌套 OBJECT 递归展开为完整点分路径再转 # 分隔
        for (decl in screen.classDecls) {
            if (decl.kind != ClassKind.OBJECT) continue
            emitObjectFuns(decl, parentPath = decl.name, funMap)
        }
        // screen / hud 级 onKey lambda：序列化 lambda body 写入保留 action 名
        val onKeyLambda = screen.onKeyLambda
        if (onKeyLambda != null) {
            // 解析阶段已校验参数数量为 1 且形参名 == LAMBDA_PARAM_NAME；此处再做一次防御
            val paramName = onKeyLambda.params.first()
            if (paramName != LAMBDA_PARAM_NAME) {
                error("onKey lambda 形参名必须为 '$LAMBDA_PARAM_NAME'，实际: '$paramName'")
            }
            val body: List<StmtAst> = onKeyLambda.body
            val bodyBytes = ExprSerializer.serializeActionBody(body)
            actionMap[SCREEN_ON_KEY_ACTION_NAME] = UiAction(SCREEN_ON_KEY_ACTION_NAME, bodyBytes)
        }
        // screen / hud 级 onKeyRelease lambda → actionMap[SCREEN_ON_KEY_RELEASE]
        // 与 onKey 完全对称:平台层只在 GLFW RELEASE(action=0)时调用本 handler。
        val onKeyReleaseLambda = screen.onKeyReleaseLambda
        if (onKeyReleaseLambda != null) {
            val paramName = onKeyReleaseLambda.params.first()
            if (paramName != ON_KEY_RELEASE_LAMBDA_PARAM_NAME) {
                error("onKeyRelease lambda 形参名必须为 '$ON_KEY_RELEASE_LAMBDA_PARAM_NAME'，实际: '$paramName'")
            }
            val body: List<StmtAst> = onKeyReleaseLambda.body
            val bodyBytes = ExprSerializer.serializeActionBody(body)
            actionMap[SCREEN_ON_KEY_RELEASE_ACTION_NAME] = UiAction(SCREEN_ON_KEY_RELEASE_ACTION_NAME, bodyBytes)
        }
        // RFC Stage 4：screen / hud 级 onMouseDown lambda → actionMap[SCREEN_ON_MOUSE_DOWN]
        // 与 onKey 完全对称的 emit 处理。
        val onMouseDownLambda = screen.onMouseDownLambda
        if (onMouseDownLambda != null) {
            // 解析阶段已校验参数数量为 1 且形参名 == ON_MOUSE_DOWN_LAMBDA_PARAM_NAME；此处兜底
            val paramName = onMouseDownLambda.params.first()
            if (paramName != ON_MOUSE_DOWN_LAMBDA_PARAM_NAME) {
                error("onMouseDown lambda 形参名必须为 '$ON_MOUSE_DOWN_LAMBDA_PARAM_NAME'，实际: '$paramName'")
            }
            val body: List<StmtAst> = onMouseDownLambda.body
            val bodyBytes = ExprSerializer.serializeActionBody(body)
            actionMap[SCREEN_ON_MOUSE_DOWN_ACTION_NAME] = UiAction(SCREEN_ON_MOUSE_DOWN_ACTION_NAME, bodyBytes)
        }
        // RFC-MOUSE-EXTENDED-EVENTS Stage A：screen / hud 级 onMouseUp lambda → actionMap[SCREEN_ON_MOUSE_UP]
        // 与 onMouseDown 完全对称的 emit 处理。
        val onMouseUpLambda = screen.onMouseUpLambda
        if (onMouseUpLambda != null) {
            // 解析阶段已校验参数数量为 1 且形参名 == ON_MOUSE_UP_LAMBDA_PARAM_NAME；此处兜底
            val paramName = onMouseUpLambda.params.first()
            if (paramName != ON_MOUSE_UP_LAMBDA_PARAM_NAME) {
                error("onMouseUp lambda 形参名必须为 '$ON_MOUSE_UP_LAMBDA_PARAM_NAME'，实际: '$paramName'")
            }
            val body: List<StmtAst> = onMouseUpLambda.body
            val bodyBytes = ExprSerializer.serializeActionBody(body)
            actionMap[SCREEN_ON_MOUSE_UP_ACTION_NAME] = UiAction(SCREEN_ON_MOUSE_UP_ACTION_NAME, bodyBytes)
        }
        // RFC-MOUSE-EXTENDED-EVENTS Stage B：screen / hud 级 onMouseMove lambda → actionMap[SCREEN_ON_MOUSE_MOVE]
        // 与 onMouseUp 完全对称的 emit 处理。
        val onMouseMoveLambda = screen.onMouseMoveLambda
        if (onMouseMoveLambda != null) {
            // 解析阶段已校验参数数量为 1 且形参名 == ON_MOUSE_MOVE_LAMBDA_PARAM_NAME；此处兜底
            val paramName = onMouseMoveLambda.params.first()
            if (paramName != ON_MOUSE_MOVE_LAMBDA_PARAM_NAME) {
                error("onMouseMove lambda 形参名必须为 '$ON_MOUSE_MOVE_LAMBDA_PARAM_NAME'，实际: '$paramName'")
            }
            val body: List<StmtAst> = onMouseMoveLambda.body
            val bodyBytes = ExprSerializer.serializeActionBody(body)
            actionMap[SCREEN_ON_MOUSE_MOVE_ACTION_NAME] = UiAction(SCREEN_ON_MOUSE_MOVE_ACTION_NAME, bodyBytes)
        }
        // Stage 1：screen 级 open 块 → actionMap[SCREEN_OPEN]
        // 顺序约定：SCREEN_ON_KEY → SCREEN_ON_MOUSE_DOWN → SCREEN_ON_MOUSE_UP → SCREEN_ON_MOUSE_MOVE → SCREEN_OPEN → SCREEN_CLOSE
        val openBody = screen.openBody
        if (openBody != null) {
            val bodyBytes = ExprSerializer.serializeActionBody(openBody)
            actionMap[SCREEN_OPEN_ACTION_NAME] = UiAction(SCREEN_OPEN_ACTION_NAME, bodyBytes)
        }
        // Stage 1：screen 级 close 块 → actionMap[SCREEN_CLOSE]
        val closeBody = screen.closeBody
        if (closeBody != null) {
            val bodyBytes = ExprSerializer.serializeActionBody(closeBody)
            actionMap[SCREEN_CLOSE_ACTION_NAME] = UiAction(SCREEN_CLOSE_ACTION_NAME, bodyBytes)
        }
        // show / hide 块 → 保留动作（每次显示 / 隐藏触发，运行时由 IrInterpreter 取出）
        val showBody = screen.showBody
        if (showBody != null) {
            val bodyBytes = ExprSerializer.serializeActionBody(showBody)
            actionMap[SCREEN_SHOW_ACTION_NAME] = UiAction(SCREEN_SHOW_ACTION_NAME, bodyBytes)
        }
        val hideBody = screen.hideBody
        if (hideBody != null) {
            val bodyBytes = ExprSerializer.serializeActionBody(hideBody)
            actionMap[SCREEN_HIDE_ACTION_NAME] = UiAction(SCREEN_HIDE_ACTION_NAME, bodyBytes)
        }
        val rootIr = elements.emit(screen.root)
        // themeTokens 由 DSL 前端 theme 块透传（已被 ThemeMergePass 合并 baseline + 覆盖字段）
        val themeTokens: Map<String, Any> = screen.theme?.tokens ?: emptyMap()
        // availableThemes：把所有已注册的 theme 预设全量写入 IR，让运行时 local.setTheme 能切换
        val availableThemes: Map<String, Map<String, Any>> = if (themeRegistry == null) {
            emptyMap()
        } else {
            val all = LinkedHashMap<String, Map<String, Any>>()
            for (scheme in themeRegistry.schemes()) {
                val tokens = themeRegistry.lookup(scheme) ?: continue
                all[scheme] = LinkedHashMap(tokens)
            }
            all
        }
        val placeholders = papi.emit(screen.papiBlocks)
        val subscribes = subscribe.emit(screen.subscribeBlocks)
        val baseIr = UiIr(
            screen.id, stateIr, actionMap, themeTokens, availableThemes,
            animationMap, funMap, emptyMap(), placeholders,
            emptyList(), emptyMap(), subscribes, rootIr,
        ).withThemeDirectory(buildThemeDirectory(themeRegistry))
        val header = screen.dslHeader
        val withHeader = baseIr
            .withDslHeader(header?.version ?: 1, header?.mode?.name?.lowercase() ?: "legacy")
            .withModel(states.emitModel(screen.model))
        // 把 sessionState / persistState 透传到 IR；空表会在 codec 层写 hasXxx=0 跳过段体。
        val withSession = screen.sessionState?.let { ast ->
            withHeader.withSessionStateDefaults(states.emit(ast))
        } ?: withHeader
        val withPersist = screen.persistState?.let { ast ->
            withSession.withPersistStateDefaults(states.emit(ast))
        } ?: withSession
        // derived 派生值：表达式序列化为 ExprBlob 透传到 IR；空块走 UiDerived.empty。
        val withDerived = screen.derived?.let { ast ->
            withPersist.withDerivedDefaults(states.emitDerived(ast))
        } ?: withPersist
        // Stage 11+：把 ScreenAst 上的 hud 顶层 meta 透传到 IR。
        val withHudMeta = buildHudMeta(screen)?.let { withDerived.withHudMeta(it) } ?: withDerived
        // 界面级默认字体：把 ScreenAst.screenFont 转成 UiScreenFont 透传到 IR
        // （注意字段顺序：UiScreenFont(name, shadow, bold, italic)；空声明在 withScreenFont 内归一为 null）。
        val withScreenFont = screen.screenFont?.let { ast ->
            withHudMeta.withScreenFont(UiScreenFont(ast.name, ast.shadow, ast.bold, ast.italic))
        } ?: withHudMeta
        // Phase 3：把 ScreenAst 上的 intercept 块透传到 IR（withInterceptRule 内已保留 screenFont）。
        val withIntercept = buildInterceptRule(screen)?.let { withScreenFont.withInterceptRule(it) } ?: withScreenFont
        val overlayTables = collectOverlays(screen.root, actionMap)
        return if (overlayTables.descriptors.isEmpty()) {
            withIntercept
        } else {
            withIntercept.withOverlays(overlayTables.descriptors, overlayTables.elementOverlays)
        }
    }

    private fun buildThemeDirectory(registry: ThemeRegistry?): List<ThemeDescriptor> {
        if (registry == null) return emptyList()
        val descriptors = registry.schemes().mapNotNull { id ->
            val tokens = registry.lookup(id) ?: return@mapNotNull null
            val metadata = registry.metadata(id)
            ThemeDescriptor(
                id,
                metadata?.displayName ?: id,
                metadata?.group ?: "外部主题",
                metadata?.order ?: 1000,
                metadata?.previewPrimary ?: colorToken(tokens, "primary", "#FF6750A4"),
                metadata?.previewSurface ?: colorToken(tokens, "surface", "#FFFFFBFE"),
                metadata?.previewOnSurface ?: colorToken(tokens, "onSurface", "#FF1C1B1F"),
            )
        }
        return descriptors.sortedWith(
            compareBy<ThemeDescriptor> { it.group }
                .thenBy { it.order }
                .thenBy { it.id },
        )
    }

    private fun colorToken(tokens: Map<String, Any>, key: String, fallback: String): String =
        (tokens[key] as? String)?.takeIf { COLOR_LITERAL.matches(it) } ?: fallback

    private fun buildHudMeta(screen: ScreenAst): UiHudMeta? {
        val tickBodyBytes = screen.hudTickBody?.let(ExprSerializer::serializeActionBody)
        // Phase 4：序列化 keybinding 块的 onTrigger 为字节码
        val keyBindings = screen.hudKeyBindings.map { kb ->
            val bytes = ExprSerializer.serializeActionBody(kb.onTrigger)
            top.wcpe.allincore.dsl.ir.model.hud.UiKeyBinding(kb.id, kb.defaultKey, kb.label, bytes)
        }
        if (screen.hudAnchor == null && screen.hudLayer == null && screen.hudHideTokens.isEmpty()
            && screen.hudZIndex == 0 && screen.hudLayout == null && screen.hudStack == null
            && screen.hudVisibility == null && tickBodyBytes == null && keyBindings.isEmpty()) {
            return null
        }
        val layout = screen.hudLayout?.let {
            HudLayoutPolicy(it.offsetX, it.offsetY, it.safeArea, it.avoidVanilla)
        } ?: HudLayoutPolicy.DEFAULT
        val stack = screen.hudStack?.let {
            HudStackPolicy(it.group, it.direction, it.gap, it.order)
        } ?: HudStackPolicy.DEFAULT
        val visibility = screen.hudVisibility?.let {
            HudVisibilityPolicy(it.whenScreen, it.allow, it.deny)
        } ?: HudVisibilityPolicy.DEFAULT
        return UiHudMeta(
            screen.hudAnchor,
            screen.hudLayer,
            screen.hudZIndex,
            screen.hudHideTokens,
            layout,
            stack,
            visibility,
            tickBodyBytes,
            keyBindings,
        )
    }

    /**
     * Phase 3：从 ScreenAst.intercept 构建 UiInterceptRule。
     * <p>intercept 块未声明时返回 null。</p>
     */
    private fun buildInterceptRule(screen: ScreenAst): UiInterceptRule? {
        val intercept = screen.intercept ?: return null
        val conditionBytes = intercept.condition?.let(ExprSerializer::serializeExpr)
        return UiInterceptRule(screen.id, intercept.menuType, conditionBytes)
    }

    private data class OverlayTables(
        val descriptors: List<OverlayDescriptor>,
        val elementOverlays: Map<String, List<Int>>,
    )

    private fun collectOverlays(root: ElementAst, parentActions: Map<String, UiAction>): OverlayTables {
        val descriptors = mutableListOf<OverlayDescriptor>()
        val elementOverlays = linkedMapOf<String, MutableList<Int>>()
        fun add(elementId: String, descriptor: OverlayDescriptor) {
            val index = descriptors.size
            descriptors += descriptor
            elementOverlays.getOrPut(elementId) { mutableListOf() } += index
        }
        fun visit(element: ElementAst) {
            if (element.kind == "modal") {
                add(element.id, modalDescriptor(element, parentActions))
            }
            element.tooltipContent?.let { tooltip ->
                when (tooltip) {
                    is TooltipContentAst.Inline -> add(element.id, tooltipInlineDescriptor(element, tooltip, parentActions))
                    is TooltipContentAst.Text -> { /* 纯文本 tooltip 走旧 prop 路径，不需要 overlay descriptor */ }
                    else -> { /* 其他形式不生成 descriptor */ }
                }
            }
            element.rightClickMenuContent?.let { add(element.id, attachedMenuDescriptor(element, it, true)) }
            element.contextMenuContent?.let { add(element.id, attachedMenuDescriptor(element, it, false)) }
            element.menuBlocks.forEach { menu -> add(element.id, menuBlockDescriptor(element, menu, parentActions)) }
            element.children.forEach(::visit)
            // 修复:for/if 体内元素在 AST 里挂在 forContent / conditionalContent 字段(不在 children)。
            // 若不递归,循环 / 条件体内 slot 的自定义 tooltip 描述符将不被收集进 elementOverlays,
            // 运行时拿不到描述符 → tooltip 渲空回退 vanilla(见 IrInterpreter 数据驱动实例化补挂)。
            element.forContent?.let { visit(it.body) }
            element.conditionalContent?.let { cc ->
                cc.branches.forEach { visit(it.body) }
                cc.elseBranch?.let(::visit)
            }
        }
        visit(root)
        return OverlayTables(descriptors, elementOverlays.mapValues { it.value.toList() })
    }

    private fun tooltipInlineDescriptor(
        element: ElementAst,
        tooltip: TooltipContentAst.Inline,
        parentActions: Map<String, UiAction>,
    ): OverlayDescriptor =
        OverlayDescriptor(
            OverlayDescriptor.OverlayKind.TOOLTIP,
            element.id,
            OverlayDescriptor.AnchorMode.BOX,
            ContentRef.Inline(inlineIr(tooltip.root, parentActions)),
            false,
            defaultAnimation(100.0),
            OverlayDescriptor.DismissBehavior.of(OverlayDescriptor.DismissBehavior.Flag.HOVER_OUT),
            OverlayDescriptor.PlacementSpec(OverlayDescriptor.PlacementSpec.Direction.BOTTOM, 0.0, 0.0, 4.0),
            // ADR-0036：把 tooltip data 句柄声明编码进 descriptor，payload 值表达式序列化为 ExprBlob。
            tooltip.dataBindings.map(::tooltipDataBindingIr),
        )

    /** ADR-0036：把编译期 tooltip data 绑定转为 IR 形态，payload 每个值表达式序列化为独立 ExprBlob。 */
    private fun tooltipDataBindingIr(binding: TooltipDataBinding): OverlayDescriptor.TooltipDataBinding {
        val payload = linkedMapOf<String, ByteArray>()
        for (entry in binding.payload) {
            payload[entry.key] = ExprSerializer.serializeExpr(entry.value)
        }
        // ADR-0036（v31）：可选 when 条件表达式同样序列化为 ExprBlob。
        val condition = binding.condition?.let { ExprSerializer.serializeExpr(it) }
        return OverlayDescriptor.TooltipDataBinding(
            binding.name,
            binding.namespace,
            binding.handler,
            payload,
            condition,
        )
    }

    private fun modalDescriptor(element: ElementAst, parentActions: Map<String, UiAction>): OverlayDescriptor = OverlayDescriptor(
        OverlayDescriptor.OverlayKind.MODAL,
        element.id,
        OverlayDescriptor.AnchorMode.SCREEN_CENTER,
        ContentRef.Inline(inlineIr(element, parentActions)),
        true,
        animationFromProps(element.props, 180.0),
        OverlayDescriptor.DismissBehavior.of(
            OverlayDescriptor.DismissBehavior.Flag.ESC,
            OverlayDescriptor.DismissBehavior.Flag.BACKDROP_CLICK,
            OverlayDescriptor.DismissBehavior.Flag.EXPLICIT,
        ),
        placementFromProps(element.props),
    )

    private fun attachedMenuDescriptor(
        host: ElementAst,
        content: TooltipContentAst.Component,
        rightClick: Boolean,
    ): OverlayDescriptor = OverlayDescriptor(
        if (rightClick) OverlayDescriptor.OverlayKind.RIGHT_CLICK_MENU else OverlayDescriptor.OverlayKind.CONTEXT_MENU,
        host.id,
        OverlayDescriptor.AnchorMode.MOUSE,
        componentContent(content),
        true,
        animationFromProps(host.props, 100.0),
        menuDismissBehavior(),
        placementFromProps(host.props),
    )

    private fun menuBlockDescriptor(
        host: ElementAst,
        menu: MenuContentAst,
        parentActions: Map<String, UiAction>,
    ): OverlayDescriptor = OverlayDescriptor(
        if (menu.kind == MenuContentAst.Kind.RIGHT_CLICK_MENU) {
            OverlayDescriptor.OverlayKind.RIGHT_CLICK_MENU
        } else {
            OverlayDescriptor.OverlayKind.CONTEXT_MENU
        },
        host.id,
        OverlayDescriptor.AnchorMode.MOUSE,
        ContentRef.Inline(inlineIr(menu, parentActions)),
        true,
        animationFromProps(menu.props, 100.0),
        menuDismissBehavior(),
        placementFromProps(menu.props),
    )

    /**
     * Overlay 子树 IR 构造：把主屏的 actions 一并注入子 IR 的 actions 字段。
     *
     * 原因：菜单 / modal 等 overlay 内的元素（如 button.onClick = openConfirm()）
     * 引用的是 screen 级 fun 名（编译期同时被注册到主屏 actionMap）。
     * 运行期 EventBindingDispatcher 通过 ctx.actions.get(actionName) 查找 stmt body，
     * 子树 IR 的 actions 必须能找到这些定义，否则点击 handler 会静默 no-op。
     */
    private fun inlineIr(element: ElementAst, parentActions: Map<String, UiAction>): UiIr {
        val node = elements.emit(element)
        return UiIr("${element.id}#overlay", top.wcpe.allincore.dsl.ir.model.UiState.empty(), parentActions, node)
    }

    private fun inlineIr(menu: MenuContentAst, parentActions: Map<String, UiAction>): UiIr {
        val node = elements.emit(menu.root)
        return UiIr("menu#overlay", top.wcpe.allincore.dsl.ir.model.UiState.empty(), parentActions, node)
    }

    private fun componentContent(content: TooltipContentAst.Component): ContentRef.Component =
        ContentRef.Component(content.name, content.props.associate { it.name to literalPropValue(it.value) })

    private fun literalPropValue(expr: ExprAst): Any? = when (expr) {
        is ExprAst.Literal.StringLit -> expr.value
        is ExprAst.Literal.NumberLit -> expr.value.toDouble()
        is ExprAst.Literal.BoolLit -> expr.value
        else -> null
    }

    private fun defaultAnimation(durationMs: Double): OverlayDescriptor.AnimationSpec =
        OverlayDescriptor.AnimationSpec(true, true, durationMs, durationMs, "easeOut")

    private fun animationFromProps(
        props: List<ElementAst.PropAssign>,
        defaultDurationMs: Double,
    ): OverlayDescriptor.AnimationSpec {
        val preset = stringProp(props, "animation", "fade-scale")
        val fade = preset == "fade" || preset == "fade-scale"
        val scale = (preset == "scale" || preset == "fade-scale") && boolProp(props, "scaleIn", true)
        val fadeIn = numberProp(props, "fadeIn", defaultDurationMs)
        val fadeOut = numberProp(props, "fadeOut", defaultDurationMs)
        return OverlayDescriptor.AnimationSpec(fade, scale, fadeIn, fadeOut, "easeOut")
    }

    private fun menuDismissBehavior(): OverlayDescriptor.DismissBehavior = OverlayDescriptor.DismissBehavior.of(
        OverlayDescriptor.DismissBehavior.Flag.ESC,
        OverlayDescriptor.DismissBehavior.Flag.OUTSIDE_CLICK,
        OverlayDescriptor.DismissBehavior.Flag.ITEM_CLICK,
    )

    private fun placementFromProps(props: List<ElementAst.PropAssign>): OverlayDescriptor.PlacementSpec {
        val direction = props.firstOrNull { it.name == "placement" }
            ?.let { (it.value as? ExprAst.Literal.StringLit)?.value }
            ?.let(::directionFromString)
            ?: OverlayDescriptor.PlacementSpec.Direction.AUTO
        val padding = numberProp(props, "padding", 4.0)
        val offsetX = numberProp(props, "offsetX", 0.0)
        val offsetY = numberProp(props, "offsetY", 0.0)
        return OverlayDescriptor.PlacementSpec(direction, padding, offsetX, offsetY)
    }

    private fun directionFromString(value: String): OverlayDescriptor.PlacementSpec.Direction = when (value) {
        "top" -> OverlayDescriptor.PlacementSpec.Direction.TOP
        "bottom" -> OverlayDescriptor.PlacementSpec.Direction.BOTTOM
        "left" -> OverlayDescriptor.PlacementSpec.Direction.LEFT
        "right" -> OverlayDescriptor.PlacementSpec.Direction.RIGHT
        else -> OverlayDescriptor.PlacementSpec.Direction.AUTO
    }

    private fun numberProp(props: List<ElementAst.PropAssign>, name: String, default: Double): Double =
        (props.firstOrNull { it.name == name }?.value as? ExprAst.Literal.NumberLit)?.value?.toDouble() ?: default

    private fun boolProp(props: List<ElementAst.PropAssign>, name: String, default: Boolean): Boolean =
        (props.firstOrNull { it.name == name }?.value as? ExprAst.Literal.BoolLit)?.value ?: default

    private fun stringProp(props: List<ElementAst.PropAssign>, name: String, default: String): String =
        (props.firstOrNull { it.name == name }?.value as? ExprAst.Literal.StringLit)?.value ?: default

    /**
     * Stage 8：递归把 OBJECT kind 的 ClassDecl 内 fun（含嵌套）写入 funMap。
     *
     * 路径表示：以点分路径累积（如 "Outer.Inner.More"），写 funMap 时再用
     * [top.wcpe.allincore.dsl.compiler.semantic.`native`.methods.UserObjectMethodDescriptor.FUN_REF_SEPARATOR]
     * 替换为 # 分隔；嵌套 DATA kind 不进 funMap。
     */
    private fun emitObjectFuns(
        decl: ClassDecl,
        parentPath: String,
        funMap: MutableMap<String, IrFun>,
    ) {
        val sep = top.wcpe.allincore.dsl.compiler.semantic.`native`.methods
            .UserObjectMethodDescriptor.FUN_REF_SEPARATOR
        val pathKey = parentPath.replace(".", sep)
        for (fn in decl.funs) {
            val key = "${pathKey}${sep}${fn.name}"
            check(!funMap.containsKey(key)) {
                "user object fun key '${key}' 与已注册 fun 冲突"
            }
            funMap[key] = funs.emit(fn)
        }
        for (nested in decl.nestedClasses) {
            if (nested.kind != ClassKind.OBJECT) continue
            emitObjectFuns(nested, "${parentPath}.${nested.name}", funMap)
        }
    }
}
